import axios from 'axios';

/**
 * Phase 12.3F (G-6) — the invited company owner's endpoints.
 *
 * ── Why plain axios and not `platformApiClient` ─────────────────────────────
 * The caller is a company owner who has no credential yet. `platformApiClient`
 * attaches a platform bearer token, and on a 401 it clears the platform session
 * and redirects to the Platform Console login. Both are wrong here: there is no
 * platform session, and the person on this page is not a platform user at all.
 * The same reasoning is why `CustomerActivationPage` uses bare axios.
 *
 * The `{ data, meta }` envelope is therefore unwrapped explicitly below, since
 * no interceptor is doing it.
 */

const API_BASE = '/api/v1';

/** What the activation page may know before a password is submitted. */
export interface OwnerInvitationDescription {
  valid: boolean;
  companyName: string | null;
  maskedEmail: string | null;
  expiresAt: string | null;
}

export interface AcceptOwnerInvitationResult {
  tenantId: string;
  tenantSlug: string;
  userId: string;
}

/** The response interceptor lives on the other client, so unwrap by hand. */
function unwrap<T>(body: unknown): T {
  if (body !== null && typeof body === 'object' && 'data' in body) {
    return (body as { data: T }).data;
  }
  return body as T;
}

export const ownerInvitationService = {
  /**
   * Checks whether an invitation is still live.
   *
   * Deliberately tolerant: a network or server failure resolves to "invalid"
   * rather than throwing, because this call only decides whether to render the
   * form or the expired notice. The authoritative check is `accept()`.
   */
  describe: async (invitationId: string): Promise<OwnerInvitationDescription> => {
    const response = await axios.get(`${API_BASE}/platform/owner-invitations/${invitationId}`);
    return unwrap<OwnerInvitationDescription>(response.data);
  },

  /**
   * Redeems the invitation and sets the owner's password.
   *
   * `invitationId` comes from the link and `token` from the email body — the
   * two halves deliberately travel by different routes, so a forwarded or
   * logged URL is not enough on its own.
   */
  accept: async (payload: {
    invitationId: string;
    token: string;
    newPassword: string;
    confirmPassword: string;
  }): Promise<AcceptOwnerInvitationResult> => {
    const response = await axios.post(`${API_BASE}/platform/owner-invitations/accept`, payload);
    return unwrap<AcceptOwnerInvitationResult>(response.data);
  },
};
